import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeftRight, Flame, CirclePlus, MapPin, Sparkles, Store, type LucideIcon } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";
import {
  BEST_DEALS_ITEM,
  PLAN_NAME,
  comparisonRows,
  planUpgradeTarget,
  type ComparisonRowId,
  type LockedItem,
} from "../utils/planUpgrade";

const ROW_ICON: Record<ComparisonRowId, LucideIcon> = {
  chains: Store,
  stores: MapPin,
  extra: CirclePlus,
  changes: ArrowLeftRight,
  bestDeals: Flame,
  hawkFinds: Sparkles,
};

const CLOSE_MS = 220;
// Ignore taps for a beat after opening, so the pointer that opened the sheet
// (desktop rows activate on pointer-down) can't land on a button in it.
const ARM_MS = 250;

interface PlanUpgradeSheetProps {
  /** What the paid user tapped: a retailer slug or "hot_deals". null hides the sheet. */
  locked: LockedItem | null;
  /** Display name of the locked retailer (ignored for Best Deals). */
  lockedName?: string;
  userRetailers?: string[] | null;
  onUpgrade: (locked: LockedItem) => void;
  onClose: () => void;
}

/**
 * A paid user tapped something their plan doesn't include (a store or the Best
 * Deals feed): name it, show what the next plan changes next to what they have
 * now, and send them to billing (real prices live there). Bottom sheet on
 * phones, centered card from md up. Mirrors pinto's PlanUpgradeSheet.
 */
export default function PlanUpgradeSheet({ locked, lockedName, userRetailers, onUpgrade, onClose }: PlanUpgradeSheetProps) {
  const { t } = useTranslation();
  // Keep the last item mounted through the closing animation.
  const [shown, setShown] = useState<{ item: LockedItem; name?: string } | null>(locked ? { item: locked, name: lockedName } : null);
  const [visible, setVisible] = useState(false);
  const armedRef = useRef(false);

  if (locked && (shown?.item !== locked || shown?.name !== lockedName)) setShown({ item: locked, name: lockedName });

  useEffect(() => {
    if (!locked) {
      setVisible(false);
      const timer = window.setTimeout(() => setShown(null), CLOSE_MS);
      return () => window.clearTimeout(timer);
    }
    armedRef.current = false;
    const frame = requestAnimationFrame(() => setVisible(true));
    const arm = window.setTimeout(() => { armedRef.current = true; }, ARM_MS);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(arm);
    };
  }, [locked]);

  useEffect(() => {
    if (!locked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [locked, onClose]);

  if (!shown) return null;

  const { item } = shown;
  const isFeed = item === BEST_DEALS_ITEM;
  const name = isFeed ? t("products.hotDealsLabel") : shown.name || item;
  const { current, target } = planUpgradeTarget(userRetailers ?? undefined, item);
  const rows = comparisonRows(current, target, item);
  const planName = PLAN_NAME[target];
  const guarded = (fn: () => void) => () => {
    if (armedRef.current) fn();
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[999999] flex items-end md:items-center justify-center md:p-4 transition-colors duration-200 ${
        visible ? "bg-black/50" : "bg-black/0"
      }`}
      onPointerDown={() => {
        if (armedRef.current) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-upgrade-sheet-title"
        className={`w-full md:max-w-md bg-white dark:bg-dark-800 md:border md:border-gray-200/80 md:dark:border-dark-700 rounded-t-[26px] md:rounded-3xl shadow-2xl px-5 md:px-6 pt-2.5 md:pt-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] md:pb-5 flex flex-col gap-3.5 transition-all duration-200 ease-out ${
          visible
            ? "translate-y-0 md:opacity-100 md:scale-100"
            : "translate-y-full md:translate-y-0 md:opacity-0 md:scale-95"
        }`}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div aria-hidden className="md:hidden self-center w-9 h-[5px] rounded-full bg-gray-200 dark:bg-white/10" />
        <div className="mt-1 md:mt-0 flex flex-col gap-1">
          <h3
            id="plan-upgrade-sheet-title"
            className="text-[23px] leading-7 font-extrabold tracking-tight text-gray-900 dark:text-white"
          >
            {t("storeMenu.title", { name, plan: planName })}
          </h3>
          <p className="text-[15px] leading-snug text-gray-500 dark:text-dark-300">
            {isFeed ? t("storeMenu.subBestDeals") : t("storeMenu.subStore", { name })}
          </p>
        </div>

        {/* Every cell of the next plan's column is tinted so tint and text line up. */}
        <div className="rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
          <div className="grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <div />
            <div className="flex items-center justify-center px-2 py-3 text-[13px] font-extrabold text-gray-500 dark:text-dark-300">
              {PLAN_NAME[current]}
            </div>
            <div className="flex items-center justify-center px-2 py-3 text-[13px] font-extrabold text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-400/[0.14]">
              {planName}
            </div>
            {rows.map((row) => {
              const Icon = ROW_ICON[row.id];
              return (
                <div key={row.id} className="contents" data-testid={`plan-row-${row.id}`}>
                  <div className="flex min-w-0 items-center gap-2 pl-3.5 pr-1 py-3 border-t border-gray-200 dark:border-white/[0.08]">
                    <Icon size={16} aria-hidden className="flex-shrink-0 text-gray-400 dark:text-dark-400" />
                    <span className="truncate text-[14px] font-semibold text-gray-900 dark:text-white">{t(row.label)}</span>
                  </div>
                  <div className="flex items-center justify-center whitespace-nowrap px-1.5 py-3 text-center text-[14px] text-gray-500 dark:text-dark-300 border-t border-gray-200 dark:border-white/[0.08]">
                    {t(row.from)}
                  </div>
                  <div className="flex items-center justify-center whitespace-nowrap px-1.5 py-3 text-center text-[14px] font-extrabold text-violet-800 dark:text-violet-200 bg-violet-50 dark:bg-violet-400/[0.14] border-t border-gray-200 dark:border-white/[0.08]">
                    {t(row.to)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-dark-300">{t("storeMenu.cancelAnytime")}</p>
        <button
          type="button"
          onClick={guarded(() => onUpgrade(item))}
          className="h-[52px] rounded-2xl bg-violet-700 hover:bg-violet-800 text-white text-[16.5px] font-bold transition-colors shadow-lg shadow-violet-900/25 cursor-pointer"
        >
          {t("storeMenu.cta", { plan: planName })}
        </button>
        <button
          type="button"
          onClick={guarded(onClose)}
          className="-mt-1.5 h-10 text-[15px] font-semibold text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-white transition-colors cursor-pointer"
        >
          {t("storeMenu.notNow")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
